import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Plus, Trash2, Loader2, Copy, ExternalLink, 
  DollarSign, Ticket, Percent
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import EventSubNav from '../../components/EventSubNav';
import { eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const EventRRPPPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [rrpps, setRRPPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    commission_percent: 10
  });

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventRes, rrppRes] = await Promise.all([
        eventsApi.getById(eventId),
        eventsApi.getRRPP(eventId).catch(() => ({ data: [] }))
      ]);
      setEvent(eventRes.data);
      setRRPPs(rrppRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRRPP = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      await eventsApi.createRRPP(eventId, formData);
      toast.success('RRPP agregado correctamente');
      setShowAddModal(false);
      setFormData({ name: '', email: '', phone: '', commission_percent: 10 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar RRPP');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRRPP = async (rrppId) => {
    if (!confirm('¿Eliminar este RRPP?')) return;
    
    try {
      await eventsApi.deleteRRPP(eventId, rrppId);
      toast.success('RRPP eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  // Calculate totals
  const totalSales = rrpps.reduce((sum, r) => sum + (r.sales_count || 0), 0);
  const totalRevenue = rrpps.reduce((sum, r) => sum + (r.total_sales_amount || 0), 0);
  const totalCommissions = rrpps.reduce((sum, r) => sum + (r.commission_earned || 0), 0);

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#1A1A1A] w-1/3 rounded" />
            <div className="h-96 bg-[#1A1A1A] rounded" />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div data-testid="event-rrpp-page">
          <EventSubNav eventTitle={event?.title} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-black uppercase tracking-tight">Equipo RRPP</h2>
                <p className="text-[#A1A1AA] text-sm">Gestiona el equipo de relaciones públicas</p>
              </div>
              <Button 
                onClick={() => setShowAddModal(true)}
                className="bg-white text-black hover:bg-[#E5E5E5]"
              >
                <Plus size={16} className="mr-2" />
                Agregar RRPP
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="card-dashboard text-center">
                <Users size={24} className="mx-auto mb-2 text-blue-500" />
                <p className="text-3xl font-black">{rrpps.length}</p>
                <p className="text-xs text-[#A1A1AA] uppercase">Total RRPP</p>
              </div>
              <div className="card-dashboard text-center">
                <Ticket size={24} className="mx-auto mb-2 text-green-500" />
                <p className="text-3xl font-black">{totalSales}</p>
                <p className="text-xs text-[#A1A1AA] uppercase">Ventas Totales</p>
              </div>
              <div className="card-dashboard text-center">
                <DollarSign size={24} className="mx-auto mb-2 text-[#D4AF37]" />
                <p className="text-3xl font-black">${totalRevenue.toLocaleString()}</p>
                <p className="text-xs text-[#A1A1AA] uppercase">Ingresos Generados</p>
              </div>
            </div>

            {/* RRPP List */}
            {rrpps.length === 0 ? (
              <div className="card-dashboard text-center py-16">
                <Users size={48} className="mx-auto mb-4 text-[#A1A1AA]" />
                <h3 className="text-xl font-black uppercase mb-2">Sin RRPP</h3>
                <p className="text-[#A1A1AA] mb-6">
                  No hay personal de relaciones públicas asignado a este evento.
                </p>
                <Button onClick={() => setShowAddModal(true)} className="bg-white text-black hover:bg-[#E5E5E5]">
                  <Plus size={16} className="mr-2" />
                  Agregar Primer RRPP
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {rrpps.map((rrpp) => (
                  <motion.div
                    key={rrpp.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card-dashboard"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-lg font-bold">{rrpp.name}</h4>
                          <span className="px-2 py-1 text-xs bg-purple-500/20 text-purple-500 font-mono">
                            {rrpp.code}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA] mb-4">
                          {rrpp.email && <span>{rrpp.email}</span>}
                          {rrpp.phone && <span>{rrpp.phone}</span>}
                          <span className="flex items-center gap-1">
                            <Percent size={14} />
                            {rrpp.commission_percent}% comisión
                          </span>
                        </div>

                        {/* Stats Row */}
                        <div className="flex gap-6">
                          <div>
                            <p className="text-xs text-[#52525B] uppercase">Ventas</p>
                            <p className="text-xl font-black">{rrpp.sales_count || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#52525B] uppercase">Ingresos</p>
                            <p className="text-xl font-black">${(rrpp.total_sales_amount || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#52525B] uppercase">Comisión Ganada</p>
                            <p className="text-xl font-black text-green-500">${(rrpp.commission_earned || 0).toLocaleString()}</p>
                          </div>
                        </div>

                        {/* Sale Link */}
                        {rrpp.sale_link && (
                          <div className="mt-4 p-3 bg-[#0A0A0A] border border-[#27272A]">
                            <p className="text-xs text-[#52525B] uppercase mb-2">Link de Venta</p>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 text-xs text-[#A1A1AA] truncate">{rrpp.sale_link}</code>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => copyToClipboard(rrpp.sale_link)}
                              >
                                <Copy size={14} />
                              </Button>
                              <a 
                                href={rrpp.sale_link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-2 hover:bg-[#27272A] rounded"
                              >
                                <ExternalLink size={14} />
                              </a>
                            </div>
                          </div>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRRPP(rrpp.id)}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </motion.div>
                ))}

                {/* Totals Footer */}
                <div className="card-dashboard bg-[#1A1A1A]">
                  <div className="flex justify-between items-center">
                    <span className="font-bold uppercase">Total Comisiones a Pagar</span>
                    <span className="text-2xl font-black text-[#D4AF37]">${totalCommissions.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Add RRPP Modal */}
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogContent className="bg-[#121212] border-[#27272A]">
              <DialogHeader>
                <DialogTitle className="text-xl font-black uppercase">Agregar RRPP</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleAddRRPP} className="space-y-4 mt-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#52525B]">
                    Nombre *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nombre del RRPP"
                    className="bg-transparent border-[#27272A]"
                    required
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#52525B]">
                    Email (opcional)
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@ejemplo.com"
                    className="bg-transparent border-[#27272A]"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#52525B]">
                    Teléfono (opcional)
                  </Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+54 11 1234-5678"
                    className="bg-transparent border-[#27272A]"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#52525B]">
                    Porcentaje de Comisión
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={formData.commission_percent}
                      onChange={(e) => setFormData({ ...formData, commission_percent: parseFloat(e.target.value) || 10 })}
                      min="0"
                      max="50"
                      className="bg-transparent border-[#27272A] w-24"
                    />
                    <span className="text-[#A1A1AA]">%</span>
                  </div>
                </div>

                <DialogFooter className="mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddModal(false)}
                    className="border-[#27272A]"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-white text-black hover:bg-[#E5E5E5]"
                  >
                    {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus size={16} className="mr-2" />}
                    Agregar RRPP
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default EventRRPPPage;
